import { MouseEvent, useCallback } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { PiPlus } from 'react-icons/pi';
import { useLocation } from 'wouter';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import {
  GenericTable,
  GenericTableColumn,
} from '@/components/GenericTable/GenericTable';
import { UserAvatar } from '@/components/UserAvatar';
import { useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { formatDate } from '@/lib/helpers';
import { offerInvoiceBadgeLabel } from '@/lib/offerInvoiceBadge';
import { urlField } from '@/lib/urlState';
import {
  getOfferStatus,
  offerStatusColor,
  offerStatusLabel,
  OfferListParams,
  TOffer,
  TOfferListItem,
  useOfferProposalOptions,
  useOffers,
} from '@/models/offers';
import { OwnerFilter } from '@/components/OwnerFilter/OwnerFilter';
import CreateOfferModal from './CreateOfferModal';
import { TestBadge } from '@/components/TestBadge';

function OfferInvoiceStatusCell({
  status,
  splitMode,
  paidServices,
  totalServices,
  currencyTotals,
}: {
  status?: string | null;
  splitMode?: string | null;
  paidServices?: number | null;
  totalServices?: number | null;
  currencyTotals?: Record<string, { paid: number; outstanding: number }> | null;
}) {
  if (!status && !currencyTotals)
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  const tone =
    status === 'fully-paid' || status === 'paid'
      ? 'green'
      : status?.startsWith('partially')
        ? 'yellow'
        : status === 'not-invoiced'
          ? 'gray'
          : 'blue';
  return (
    <Stack gap={2}>
      {currencyTotals &&
        Object.entries(currencyTotals).map(([cur, t]) => (
          <Text key={cur} size="xs">
            {t.paid + t.outstanding} {cur}
          </Text>
        ))}
      {status && (
        <Group gap={6} wrap="nowrap">
          <Badge color={tone} variant="light" size="sm">
            {offerInvoiceBadgeLabel(status)}
          </Badge>
          {splitMode === 'split' &&
            totalServices != null &&
            totalServices > 0 && (
              <Text size="xs" c="dimmed">
                {paidServices ?? 0} of {totalServices} installments paid
              </Text>
            )}
        </Group>
      )}
    </Stack>
  );
}

const kycColor: Record<string, string> = {
  in_progress: 'yellow',
  approved: 'green',
  rejected: 'red',
  retry: 'orange',
};

const splitStatusColor: Record<string, string> = {
  draft: 'gray',
  proposed: 'blue',
  returned: 'orange',
  approved: 'teal',
  committed: 'green',
};

const splitStatusLabel: Record<string, string> = {
  draft: 'Draft',
  proposed: 'Awaiting BizDev',
  returned: 'Returned',
  approved: 'Approved',
  committed: 'Committed',
};

const SORT_COLUMNS: Record<string, string> = {
  title: 'title',
  createdAt: 'created_at',
};

type OfferSortField = 'createdAt' | 'title';

const OFFERS_SCHEMA = {
  scope: urlField.enum(['live', 'test', 'all'] as const, 'live'),
  owners: urlField.numberArray(),
  cps: urlField.numberArray(),
  accepted: urlField.enum(['', 'true', 'false'] as const, ''),
  split: urlField.enum(
    ['', 'draft', 'proposed', 'returned', 'approved', 'committed'] as const,
    '',
  ),
  contactId: urlField.string(),
  archived: urlField.boolean(),
  sortBy: urlField.enum(['createdAt', 'title'] as const, 'createdAt'),
  sortOrder: urlField.enum(['asc', 'desc'] as const, 'desc'),
  page: urlField.page(),
  limit: urlField.number(20, { integer: true, min: 1, max: 200 }),
};

export default function OfferListPage() {
  const [, nav] = useLocation();
  const [state, setState, { clear, isFiltered }] = useUrlState(OFFERS_SCHEMA);
  const [contactIdInput, setContactIdInput] = useUrlDraft(
    state.contactId,
    (contactId) => setState({ contactId }),
  );

  const contactIdNumber = Number(state.contactId);

  const queryParams: OfferListParams = {
    page: state.page,
    limit: state.limit,
    sortBy: SORT_COLUMNS[state.sortBy] ?? 'created_at',
    sortOrder: state.sortOrder,
    scope: state.scope,
    includeArchived: state.archived || undefined,
    ownerUserId: state.owners.length ? state.owners : undefined,
    cpId: state.cps.length ? state.cps : undefined,
    accepted:
      state.accepted === 'true'
        ? true
        : state.accepted === 'false'
          ? false
          : undefined,
    contactId:
      Number.isInteger(contactIdNumber) && contactIdNumber > 0
        ? contactIdNumber
        : undefined,
    splitPlanStatus: state.split || undefined,
  };

  const { data, isLoading } = useOffers(queryParams);
  const { data: proposalOptions, isLoading: proposalOptionsLoading } =
    useOfferProposalOptions();
  const [
    createModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);

  const handleCreated = (offer: TOffer) => {
    closeCreateModal();
    nav(`/${offer.id}`);
  };

  const handleSort = useCallback(
    (field: string) => {
      if (!SORT_COLUMNS[field]) return;
      setState({
        sortBy: field as OfferSortField,
        sortOrder:
          state.sortBy === field && state.sortOrder === 'asc' ? 'desc' : 'asc',
        page: 1,
      });
    },
    [setState, state.sortBy, state.sortOrder],
  );

  const stop = (e: MouseEvent) => e.stopPropagation();
  const dash = (
    <Text fz="xs" c="dimmed">
      —
    </Text>
  );

  const columns: GenericTableColumn<TOfferListItem>[] = [
    {
      accessor: 'title',
      title: 'Title',
      sortable: true,
      wrap: true,
      maxWidth: 280,
      render: (r) => (
        <Group gap={6} wrap="nowrap">
          <Text fz="sm" fw={500}>
            {r.title}
          </Text>
          {r.isTest && <TestBadge />}
        </Group>
      ),
    },
    {
      accessor: 'accountName',
      title: 'Account',
      render: (r) =>
        r.accountId && r.accountName ? (
          <Anchor
            component="a"
            href={`/accounts/${r.accountId}`}
            fz="xs"
            onClick={stop}
          >
            {r.accountName}
          </Anchor>
        ) : (
          dash
        ),
    },
    {
      accessor: 'contactName',
      title: 'Contact',
      render: (r) => (
        <Anchor
          component="a"
          href={`/contacts/${r.contactId}`}
          fz="xs"
          onClick={stop}
        >
          {r.contactName}
        </Anchor>
      ),
    },
    {
      accessor: 'contactEmail',
      title: 'Email',
      render: (r) => <Text fz="xs">{r.contactEmail}</Text>,
    },
    {
      accessor: 'itemCount',
      title: 'Items',
      render: (r) => <Text fz="xs">{r.itemCount}</Text>,
    },
    {
      accessor: 'accepted',
      title: 'Status',
      render: (r) => {
        const { status } = getOfferStatus(r);
        return (
          <Group gap={4} wrap="nowrap">
            <Badge size="sm" color={offerStatusColor[status]}>
              {offerStatusLabel[status]}
            </Badge>
            {r.kycStatus && r.kycStatus !== 'pending' && (
              <Badge
                size="sm"
                variant="light"
                color={kycColor[r.kycStatus] ?? 'gray'}
              >
                KYC
              </Badge>
            )}
            {r.archivedAt && (
              <Badge size="sm" variant="outline" color="gray">
                Archived
              </Badge>
            )}
          </Group>
        );
      },
    },
    {
      accessor: 'currencyTotals',
      title: 'SUM',
      render: (r) => (
        <OfferInvoiceStatusCell
          status={r.invoiceStatus}
          splitMode={r.splitMode}
          paidServices={r.paidServices}
          totalServices={r.totalServices}
          currencyTotals={r.currencyTotals}
        />
      ),
    },
    {
      accessor: 'splitPlanStatus',
      title: 'Split',
      render: (r) =>
        r.splitPlanStatus ? (
          <Badge
            size="sm"
            variant="light"
            color={splitStatusColor[r.splitPlanStatus] ?? 'gray'}
          >
            {splitStatusLabel[r.splitPlanStatus] ?? r.splitPlanStatus}
          </Badge>
        ) : (
          dash
        ),
    },
    {
      accessor: 'discountPercentage',
      title: 'Discount',
      render: (r) =>
        r.discountPercentage > 0 ? (
          <Text fz="xs">{r.discountPercentage}%</Text>
        ) : (
          dash
        ),
    },
    {
      accessor: 'createdByName',
      title: 'Creator',
      render: (r) =>
        r.createdByEmail || r.createdByName ? (
          <UserAvatar
            name={r.createdByName}
            email={r.createdByEmail}
            picture={r.createdByPicture}
            size={24}
          />
        ) : (
          dash
        ),
    },
    {
      accessor: 'expiresAt',
      title: 'Expires',
      render: (r) => {
        if (!r.expiresAt) return dash;
        const past = new Date(r.expiresAt) < new Date();
        return (
          <Text fz="xs" c={past ? 'red' : undefined}>
            {formatDate(r.expiresAt)}
          </Text>
        );
      },
    },
    {
      accessor: 'createdAt',
      title: 'Created',
      sortable: true,
      render: (r) => <Text fz="xs">{formatDate(r.createdAt)}</Text>,
    },
  ];

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / state.limit);

  return (
    <Stack gap="md">
      <CreateOfferModal
        opened={createModalOpened}
        onClose={closeCreateModal}
        onCreated={handleCreated}
      />
      <Group justify="space-between">
        <Text fw={600} fz="lg">
          Offers
        </Text>
        <Button
          leftSection={<PiPlus size={14} />}
          size="xs"
          onClick={openCreateModal}
        >
          New Offer
        </Button>
      </Group>

      <Group gap="xs">
        <SegmentedControl
          size="xs"
          value={state.scope}
          onChange={(v) => setState({ scope: v as typeof state.scope })}
          data={[
            { label: 'Live', value: 'live' },
            {
              label: `Test${data ? ` (${data.counts.test})` : ''}`,
              value: 'test',
            },
            { label: 'All', value: 'all' },
          ]}
        />
        <OwnerFilter
          value={state.owners}
          onChange={(owners) => setState({ owners })}
          w={220}
        />
        <MultiSelect
          size="xs"
          placeholder={state.cps.length ? undefined : 'Commercial proposal'}
          searchable
          clearable
          disabled={proposalOptionsLoading}
          data={(proposalOptions?.proposals ?? []).map((p) => ({
            value: String(p.id),
            label: `${p.name} (${p.jurisdiction})`,
          }))}
          value={state.cps.map(String)}
          onChange={(v) => setState({ cps: v.map(Number) })}
          w={260}
        />
        <Select
          size="xs"
          placeholder="Status"
          data={[
            { value: 'true', label: 'Accepted' },
            { value: 'false', label: 'Not accepted' },
          ]}
          value={state.accepted || null}
          onChange={(v) =>
            setState({ accepted: (v ?? '') as typeof state.accepted })
          }
          clearable
          w={140}
        />
        <Select
          size="xs"
          placeholder="Split schedule"
          data={[
            { value: 'proposed', label: 'Awaiting BizDev' },
            { value: 'returned', label: 'Returned to SR' },
            { value: 'approved', label: 'Approved' },
            { value: 'committed', label: 'Committed' },
            { value: 'draft', label: 'Draft' },
          ]}
          value={state.split || null}
          onChange={(v) => setState({ split: (v ?? '') as typeof state.split })}
          clearable
          w={170}
        />
        <Button
          size="xs"
          variant={state.split === 'proposed' ? 'filled' : 'light'}
          onClick={() =>
            setState({ split: state.split === 'proposed' ? '' : 'proposed' })
          }
        >
          Awaiting split approval
        </Button>
        <TextInput
          size="xs"
          placeholder="Contact ID"
          value={contactIdInput}
          onChange={(e) =>
            setContactIdInput(e.currentTarget.value.replace(/\D/g, ''))
          }
          w={120}
        />
        <Checkbox
          size="xs"
          label="Show archived?"
          checked={state.archived}
          onChange={(e) => setState({ archived: e.currentTarget.checked })}
        />
        <ClearFiltersButton visible={isFiltered} onClick={clear} />
      </Group>

      <GenericTable
        records={data?.offers ?? []}
        columns={columns}
        loading={isLoading}
        sortBy={state.sortBy}
        sortOrder={state.sortOrder}
        onSort={handleSort}
        emptyMessage="No offers found"
        onRowClick={(row) => nav(`/${(row as TOfferListItem).id}`)}
        minWidth={1400}
      />

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination
            size="sm"
            total={totalPages}
            value={state.page}
            onChange={(page) => setState({ page })}
          />
        </Group>
      )}
    </Stack>
  );
}
